import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Icon, IconButton, Text, VStack} from '@chakra-ui/react';
import {useApi} from "@/uses/useApi";
import useScreenState from "@/uses/useScreenState";
import {
    ResourceBody,
    ResourceBodySkeleton,
    CreateOrEditResource,
    EntityListBody,
    EntityListBodySkeleton,
    EntityListHeader,
    EntityListHeaderSkeleton,
    EntityModalContent,
    ListHeader,
    Paginator,
    PaginatorSkeleton
} from "@/components";
import {useModal} from "@/contexts/modalContext";
import {useNavigate, useSearchParams} from "react-router-dom";
import {useLanguage} from "@/contexts/languageContext";
import objectUtil from "@/utils/objectUtil";
import {Plus} from 'lucide-react';
import colorUtil from "@/utils/colorUtil";

const PER_PAGE = 20;

export const ResourceList = () => {
    const navigate = useNavigate();
    const {language, availableLanguages, t} = useLanguage();
    const sizes = {base: '95%', md: '95%', lg: '90%'};
    const api = useApi();
    const [entities, setEntities] = useState([]);
    const [displayLanguage, setDisplayLanguage] = useState(language);

    const languageOptions = availableLanguages.map((lang) => ({
        label: lang === 'fr' ? '🇫🇷 Français' : '🇬🇧 English',
        value: lang
    }));

    const possibleSorts = [
        ['categorySortOrderPath', 'asc', t('sentences.sortByField', { field: t('objects.category_one'), order: t('orders.asc') })],
        ['createdAt', 'desc', t('sentences.sortByField', { field: t('fields.createdAt'), order: t('orders.desc') })],
        ['createdAt', 'asc', t('sentences.sortByField', { field: t('fields.createdAt'), order: t('orders.asc') })],
    ]

    const {open, close} = useModal();
    const [itemNumber, setItemNumber] = useState(null);
    const [categories, setCategories] = useState(null);

    const [currentQueryParameters, setSearchParams] = useSearchParams();
    const getDefaultState = useCallback((pageParams) => {
        const result = {}

        /** Retrieve filters param */
        const filter = pageParams.get('filter');
        try {
            result.filter = JSON.parse(filter);
            if (objectUtil.isObject(result.filter)) {
                result.filter = Object.fromEntries(Object.entries(result.filter).filter(([_, value]) => (value ?? []).length > 0));
            }
        } catch (e) {
            console.error(e);
            result.filter = [];
        }
        /** Retrieve search param */
        result.search = pageParams.get('search') ?? '';

        /** Retrieve sort param */
        const sort = (pageParams.get('sort') ?? '').split('-');
        result.sort = possibleSorts.find(([col, type]) => sort[0] === col && sort[1] === type) ?? possibleSorts?.[0];

        /** Retrieve page param */
        const parsedPage = parseInt(pageParams.get('page'), 10);
        if (isNaN(parsedPage) || parsedPage <= 0) {
            result.page = 0;
        } else {
            result.page = parsedPage - 1;
        }
        return result;
    }, []);
    const defaultParams = getDefaultState(currentQueryParameters);
    const [filters, setFilters] = useState([]);
    const [getParams, setGetParams] = useState({
        sort: defaultParams.sort,
        ...(defaultParams.page > 0 ? {page: defaultParams.page} : {}),
        ...(defaultParams.search && defaultParams.search.length >= process.env.REACT_APP_MIN_SEARCH_LENGTH ? {search: defaultParams.search} : {}),
        ...(Object.keys(defaultParams.filter ?? {}).length > 0 ? {filter: defaultParams.filter} : {}),
    });

    const {state, realState, isMounted, setState} = useScreenState();


    const reqParams = useRef(null);
    const isCategorySort = useCallback((params) => params?.sort?.[0] === 'categorySortOrderPath', []);

    const getCategorySortPath = useCallback((entity, categoriesById) => {
        return (entity?.categories ?? [])
            .map((categoryId) => categoriesById?.[categoryId]?.entity)
            .filter(Boolean)
            .reverse()
            .map((category) => ({
                id: category.id,
                sortOrder: category.sortOrder ?? Number.MAX_SAFE_INTEGER,
            }));
    }, []);

    const compareResourceCategorySortOrder = useCallback((left, right, categoriesById) => {
        const fallbackSortOrder = Number.MAX_SAFE_INTEGER;
        const leftSortPath = getCategorySortPath(left, categoriesById);
        const rightSortPath = getCategorySortPath(right, categoriesById);
        const maxDepth = Math.max(leftSortPath.length, rightSortPath.length);

        for (let i = 0; i < maxDepth; i++) {
            const leftCategory = leftSortPath[i];
            const rightCategory = rightSortPath[i];
            const sortDifference = (leftCategory?.sortOrder ?? fallbackSortOrder) - (rightCategory?.sortOrder ?? fallbackSortOrder);
            if (sortDifference !== 0) {
                return sortDifference;
            }

            const idDifference = (leftCategory?.id ?? fallbackSortOrder) - (rightCategory?.id ?? fallbackSortOrder);
            if (idDifference !== 0) {
                return idDifference;
            }
        }

        return 0;
    }, [getCategorySortPath]);

    const prepareEntities = useCallback((hits, categoriesToBind) => {
        return hits.filter((entity) => {
            const titleField = `${displayLanguage}Title`;
            return entity[titleField] && entity[titleField].length > 0;
        }).map((entity) => {
            return {
                ...entity,
                displayLanguage,
                categories: [...entity.categories].reverse().map((category) => {
                    return availableLanguages.reduce((acc, lang) => ({
                        ...acc,
                        color: categoriesToBind?.[category]?.color,
                        [lang]: categoriesToBind?.[category]?.entity?.[`${lang}Label`] ?? ''
                    }), {})
                })
            }
        });
    }, [availableLanguages, displayLanguage]);

    const getPage = useCallback(() => {
        /** If page is different, it's only a pagination action,
         * so no need to show skeleton for paginator
         */
        const newState = reqParams.current !== null &&
        objectUtil
            .difference(getParams, reqParams.current ?? {})
            .filter((e) => !['page', 'sort'].includes(e))
            .length !== 0
            ? 1 : 0;
        setState((prevState) => prevState === -1 ? prevState : newState);
        reqParams.current = getParams
        const shouldSortByCategory = isCategorySort(reqParams.current);

        return api.ApiService.get('resource', {
            ...reqParams.current,
            limit: PER_PAGE,
            locale: displayLanguage,
            ...(state === -1 ? {needFilters: 'bool:true'} : {})
        })
            .then(async res => {
                if (res?.data?.type === 'success') {
                    let categoriesToBind = categories;
                    if (res?.data?.value?.facetDistribution) {
                        const categoryColors = colorUtil.generateBeautifulColorPairs(Object.keys((res?.data?.value?.facetDistribution?.categories ?? {})).length);
                        categoriesToBind = Object.fromEntries(Object.entries((res?.data?.value?.facetDistribution?.categories ?? {})).map(([key, value], i) => [
                            key,
                            {
                                ...value,
                                color: categoryColors[i]
                            }
                        ]));
                        setCategories(() => categoriesToBind);
                        setFilters(() => {
                            const items = Object.keys(categoriesToBind).map((key) => {
                                const category = categoriesToBind[key].entity;
                                return {
                                    label: `${category?.[`${language}Label`]} (${categoriesToBind[key].records})`,
                                    value: parseInt(key),
                                    color: categoriesToBind[key].color,
                                    parent: category?.parent,
                                }
                            });
                            return {
                                'categories': {
                                    nested: true,
                                    placeholder: `${t('sentences.actionField', {action: t('actions.filterBy'), element: t('objects.category_one')})}`,
                                    items: objectUtil.plainToTree(items,'value','parent')
                                }
                            }
                        })
                    }

                    if (shouldSortByCategory) {
                        const allResourcesRes = await api.ApiService.get('resource', {
                            ...objectUtil.filterByKeys(reqParams.current, ['page'], false),
                            getAll: 'bool:true',
                            locale: displayLanguage,
                        });
                        const allHits = allResourcesRes?.data?.type === 'success' ? allResourcesRes.data.value : [];
                        const sortedHits = allHits.map((entity, index) => ({entity, index}))
                            .sort((left, right) => {
                                return compareResourceCategorySortOrder(left.entity, right.entity, categoriesToBind) || left.index - right.index;
                            })
                            .map(({entity}) => entity);
                        const page = reqParams.current?.page ?? 0;

                        setItemNumber(() => sortedHits.length);
                        setEntities(() => prepareEntities(sortedHits.slice(page * PER_PAGE, (page + 1) * PER_PAGE), categoriesToBind));
                    } else {
                        setItemNumber(() => res?.data?.value?.estimatedTotalHits);
                        setEntities(() => prepareEntities(res?.data?.value?.hits ?? [], categoriesToBind));
                    }
                }
            })
            .catch(e => console.log(e?.response?.data))
            .finally(() => setState(_ => 2))
    }, [api.ApiService, categories, compareResourceCategorySortOrder, displayLanguage, getParams, isCategorySort, prepareEntities, state]);

    useEffect(() => {
        getPage().then(() => {
            /** Manage persistent state of search query */
            setSearchParams(prev => {
                if ('search' in getParams) {
                    prev.set('search', getParams.search);
                } else {
                    prev.delete('search');
                }

                if ('filter' in getParams) {
                    prev.set('filter', JSON.stringify(getParams.filter));
                } else {
                    prev.delete('filter');
                }

                if ('sort' in getParams) {
                    prev.set('sort', getParams.sort.slice(0, 2).join('-'));
                } else {
                    prev.delete('sort');
                }

                if ('page' in getParams) {
                    prev.set('page', (getParams.page + 1).toString());
                } else {
                    prev.delete('page');
                }

                return prev;
            })
        })
    }, [getParams, displayLanguage]);


    const hasData = useMemo(() => {
        return isMounted && (entities.length > 0 || Object.keys(objectUtil.filterByKeys(getParams, ['sort'], false)).length > 0)
    }, [isMounted, entities, getParams]);

    const noDataText = t('sentences.noEntityYet.resource');

    const Table = useMemo(() => {
        return hasData
            ?
            <VStack position="relative" w="100%" overflow="hidden" flex={1} pb="2vh">
                <EntityListHeader
                    after={<IconButton
                        bg={"secondary.500"}
                        onClick={(_) => {
                            open(<EntityModalContent
                                title={t('actions.add')}
                                Body={<CreateOrEditResource
                                    successCallback={() => {
                                        navigate(0);
                                    }}
                                />}
                                close={close}
                            />, 'cover')
                        }}
                    >
                        <Icon as={Plus}/>
                    </IconButton>}
                    setGetParams={setGetParams}
                    filters={filters}
                    defaultParams={defaultParams}
                    sizes={sizes}
                    possibleSorts={possibleSorts}
                    displayLanguage={displayLanguage}
                    setDisplayLanguage={setDisplayLanguage}
                    languageOptions={languageOptions}
                />
                <VStack
                    flex={1}
                    w={sizes}
                    py="1rem"
                    align="center"
                    justify="flex-start"
                    overflow="auto"
                    color="secondary.500"
                >
                    {
                        [0, 1].includes(realState)
                            ? <EntityListBodySkeleton
                                Body={ResourceBodySkeleton}
                                sizes={sizes}/>
                            : <EntityListBody
                                noDataText={noDataText}
                                entity="resource"
                                Body={ResourceBody}
                                EditBody={CreateOrEditResource}
                                sizes={sizes}
                                entities={entities}
                                setEntities={setEntities}
                                getDeletionText={(e) => `"${e?.[`${language}Title`]}"`.slice(0,200) }
                            />
                    }
                </VStack>
                {
                    realState === 1
                        ? <PaginatorSkeleton/>
                        : <Paginator
                            defaultPage={defaultParams.page}
                            setGetParams={setGetParams}
                            itemNumber={itemNumber}
                            perPage={PER_PAGE}
                        />
                }
            </VStack>
            :
            <VStack align="center" justify="center">
                <Text>{noDataText}</Text>
                <IconButton
                    bg={'secondary.500'}
                    onClick={(_) => {
                        open(<EntityModalContent
                            title={t('actions.add')}
                            Body={<CreateOrEditResource
                                successCallback={() => {
                                    navigate(0);
                                }}
                            />}
                            close={close}
                        />, 'cover')
                    }}
                >
                    <Icon as={Plus}/>
                </IconButton>
            </VStack>
    }, [hasData, realState, entities, setEntities, setGetParams, filters, defaultParams, sizes, possibleSorts, itemNumber, close, navigate])

    const Content = useMemo(() => {
        return isMounted
            ? Table
            : <VStack w="100%" overflow="hidden" flex={1} pb="2vh">
                <EntityListHeaderSkeleton
                    sizes={sizes}
                    after={<IconButton/>}
                />
                <VStack
                    flex={1}
                    w={sizes}
                    py="1rem"
                    align="center"
                    justify="flex-start"
                    overflow="auto"
                    color="secondary.500"
                >
                    <EntityListBodySkeleton
                        Body={ResourceBodySkeleton}
                        sizes={sizes}/>
                </VStack>
                <PaginatorSkeleton/>
            </VStack>
    }, [isMounted, Table])

    return (
        <VStack w="100%" overflow="hidden">
            <ListHeader title={t('sentences.managementOfEntities', { entity: t('objects.resource_other') })} description={t('sentences.manageEntities', { action1: t('actions.add'), action2: t('actions.edit'), action3: t('actions.view'), entity: t('objects.resource_other')})}/>
            {
                /** Wait for options to be retrieved otherwise, default state isn't took in count */
                Content
            }
        </VStack>
    );
};

export default ResourceList;
